/**
 * デッキのカードの型番・レアリティを選ぶ（Issue #954）。押したカード 1 枚に効く。「同名カードにも適用する」で同じカードの全部に。
 * 候補はそのカードの収録パック（`GetCardSetCodes`）と、登録済みの画像の型番（`GetCardImage`）。上で言語（日本語 / English）を切り替え、
 * その言語の型番だけを出す。最初は画像のある型番だけ、「全ての型番を表示」で画像の無い型番も（Issue #957）。
 * 版は上がらず、デッキのどの版（＝過去の試合）にも効く。
 */
import { useEffect, useState } from 'react';
import { api } from '../../api/api';

// 収録パックの地域（card_packs.locale）→ 絵の言語。型番の地域（JP / EN / AE）があればそちらが先
const LOCALE_LANG = { ja: 'jp', jp: 'jp', ae: 'en', en: 'en', na: 'en', eu: 'en' };
const REGION_LANG = { JP: 'jp', EN: 'en', AE: 'en' };
const langOfCode = (code, fallback) => REGION_LANG[String(code ?? '').match(/-([A-Z]{2})[A-Z]?\d/)?.[1]] ?? fallback;

/** 選べる (型番, レアリティ) の一覧（純関数）。各行に言語 lang。画像のある組を先に */
export function printingOptions(candidates, images) {
  const out = new Map();
  const key = (s, r) => `${s}|${r ?? ''}`;
  for (const img of images ?? []) {
    if (!img.set_code) continue;
    out.set(key(img.set_code, img.rarity), { set_code: img.set_code, rarity: img.rarity ?? '', pack_name: null, image: img,
      lang: langOfCode(img.set_code, img.language_code) });
  }
  for (const c of candidates ?? []) {
    for (const r of (c.rarities?.length ? c.rarities : [''])) {
      const k = key(c.set_code, r);
      const prev = out.get(k);
      out.set(k, { set_code: c.set_code, rarity: r, pack_name: c.pack_name, image: prev?.image ?? null,
        lang: prev?.lang ?? langOfCode(c.set_code, LOCALE_LANG[c.locale] ?? 'jp') });
    }
  }
  return [...out.values()].sort((a, b) => Number(!a.image) - Number(!b.image) || a.set_code.localeCompare(b.set_code));
}

/** 画面に出す行（純関数）: その言語だけ、all でなければ画像のある型番だけ */
export function visibleOptions(options, lang, all) {
  return options.filter((o) => o.lang === lang && (all || o.image));
}

export default function PrintingModal({ card, slots, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [images, setImages] = useState([]);
  const [sameName, setSameName] = useState(false);   // 同名カード（同じデッキの同じカード）の全部に当てる
  const [saving, setSaving] = useState(false);
  // 言語: 今の絵の言語から（型番が無ければ日本語）
  const [lang, setLang] = useState(['jp', 'en'].includes(card.language_code) ? card.language_code : 'jp');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.project('GetCardSetCodes', { card_id: String(card.card_id) }).then((r) => setCandidates(Array.isArray(r) ? r : [])).catch(() => {});
    api.project('GetCardImage', { card_id: String(card.card_id) }).then((r) => setImages(Array.isArray(r) ? r : [])).catch(() => {});
  }, [card.card_id]);

  const apply = async (setCode, rarity) => {
    setSaving(true);
    try {
      const ids = sameName ? slots.map((s) => s.deck_card_id) : [card.deck_card_id];
      await api.project('SetDeckCardPrinting', { slot_ids: ids, set_code: setCode, rarity });
      onDone();
    } catch (err) {
      alert('変えられませんでした: ' + err.message);
      setSaving(false);
    }
  };

  const options = printingOptions(candidates, images);
  const shown = visibleOptions(options, lang, showAll);
  const hidden = options.filter((o) => o.lang === lang).length - shown.length;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560, width: '95vw' }} onClick={(e) => e.stopPropagation()} data-testid="printing-modal">
        <h2 className="modal-title">{card.card_name} — 型番・レアリティ</h2>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }} data-testid="printing-lang">
          {[['jp', '日本語'], ['en', 'English']].map(([k, label]) => (
            <button key={k} className={`btn btn-sm ${lang === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLang(k)}
              data-testid={`printing-lang-${k}`}>{label}（{options.filter((o) => o.lang === k && o.image).length}）</button>
          ))}
        </div>
        <div className="card-meta" style={{ marginBottom: 6 }} data-testid="printing-now">
          今: {card.set_code ? `${card.set_code} ${card.rarity ?? ''}` : '型番の指定なし'}
        </div>
        {slots.length > 1 && (
          <label className="card-meta" style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
            <input type="checkbox" checked={sameName} onChange={(e) => setSameName(e.target.checked)} data-testid="printing-same-name" />
            この変更を同名カードにも適用する（{slots.length} 枚）
          </label>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '50vh', overflowY: 'auto' }}>
          {shown.length === 0 && (
            <p className="loading">{hidden > 0 ? 'この言語の画像のある型番はありません' : 'この言語の型番の候補がありません'}</p>
          )}
          {shown.map((o) => (
            <button key={`${o.set_code}|${o.rarity}`} className="btn btn-secondary btn-sm" disabled={saving}
              style={{ display: 'flex', gap: 8, alignItems: 'center', textAlign: 'left', justifyContent: 'flex-start' }}
              onClick={() => apply(o.set_code, o.rarity)} data-testid={`printing-${o.set_code}-${o.rarity}`}>
              {o.image?.download_url
                ? <img src={o.image.download_url} alt="" style={{ width: 28, borderRadius: 2 }} />
                : <span style={{ width: 28, display: 'inline-block' }} />}
              <strong>{o.set_code}</strong>
              <span>{o.rarity || '—'}</span>
              <span className="card-meta" style={{ flex: 1 }}>{o.pack_name ?? ''}</span>
              <span style={{ fontSize: '0.7rem', color: o.image ? '#6e9' : '#888' }}>{o.image ? '絵あり' : '絵なし'}</span>
            </button>
          ))}
          {!showAll && hidden > 0 && (
            <button className="btn btn-sm btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setShowAll(true)} data-testid="printing-show-all">
              全ての型番を表示（画像の無い型番 {hidden} 件）
            </button>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" disabled={saving} onClick={() => apply('', '')}>指定をはずす</button>
          <button className="btn btn-secondary" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
}
